import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CaretIcon, CloseIcon } from '../assets/icons';
import { Spinner } from './ui';

/**
 * A dropdown that looks like a Select input but opens an arbitrary panel (a tree)
 * instead of a plain option list. Closes on outside click; the render-prop child
 * receives a `close` callback so a single-select tree can dismiss on choose.
 */
export default function TreeDropdown({
  placeholder,
  valueLabel,
  invalid,
  disabled,
  loading,
  onClear,
  panelClassName = 'w-full',
  children,
}: {
  placeholder: string;
  valueLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** Show a spinner inside the panel while the tree data is being fetched. */
  loading?: boolean;
  /** When provided and a value is set, a clear (×) button is shown in the trigger. */
  onClear?: () => void;
  /** Extra classes for the floating panel (e.g. a wider fixed width). */
  panelClassName?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={[
          'flex h-11 w-full items-center justify-between gap-2 rounded-md border bg-white px-[13px] text-left text-base shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-[#f8fafc]',
          invalid
            ? 'border-red-400 focus:ring-red-100'
            : 'border-line focus:border-brand focus:ring-brand/15',
        ].join(' ')}
      >
        <span className={['min-w-0 truncate', valueLabel ? 'text-ink' : 'text-[#6b7280]'].join(' ')}>
          {valueLabel || placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {onClear && valueLabel && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onClear();
              }}
              className="flex size-4 items-center justify-center rounded text-[#9ca3af] transition-colors hover:text-ink"
            >
              <CloseIcon className="size-3" />
            </span>
          )}
          <CaretIcon
            className={['size-4 text-[#9ca3af] transition-transform', open ? 'rotate-180' : ''].join(' ')}
          />
        </span>
      </button>

      {open && (
        <div
          className={[
            'absolute left-0 top-full z-30 mt-1 max-h-[60vh] max-w-[90vw] overflow-y-auto rounded-xl border border-line bg-white p-2 shadow-[0_12px_32px_rgba(0,0,0,0.14)]',
            panelClassName,
          ].join(' ')}
        >
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-ink-secondary">
              <Spinner size={16} /> Loading…
            </div>
          ) : (
            children(() => setOpen(false))
          )}
        </div>
      )}
    </div>
  );
}
